/**
 * `/settings` — the factory's stationery.
 *
 * The furniture every document stands on: the letterhead, the file-number and
 * date lines, the secrecy grading, the addressee stack, the routing line, the
 * approval ladder, the signature block and the annexure spine. Set once here,
 * referenced by every template, never retyped into one.
 *
 * **This screen exists because the old one showed only key and version.** An
 * administrator could see that `letterhead` was at v2 and unconfirmed, and had
 * no way to read what it said — which is a strange thing to ask somebody to
 * confirm. Every card here renders the block as it will print.
 *
 * Two rules carried from the template editor, for the same reasons:
 *
 * * **Publishing affects future documents only.** A document already generated
 *   keeps the stationery it was made with, so an old noting reprints with the
 *   letterhead of its own year rather than this year's.
 * * **Confirmation is separate from editing.** The seeded blocks were read off
 *   real samples, which is evidence of what this office prints, not the
 *   office's word that it is current. Until somebody signs one off, every
 *   document carries office text nobody has confirmed — and says so.
 */

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../api/client'
import type { OrgBlockView } from '../api/client'
import { PublishBox } from '../components/editor'
import { ErrorEnvelope } from '../components/bits'
import { readDay } from '../lib/blocks'

/**
 * The order a reader meets these on a page, not alphabetical order.
 *
 * `letterhead` first because it is the top of the sheet; `signature_ladder` and
 * `signature_block` last because they are the foot. A key the server sends that
 * is not listed here still renders — it sorts to the end rather than vanishing,
 * because a block missing from a screen is a block nobody confirms.
 */
const ORDER = [
  'letterhead',
  'file_number_line',
  'date_line',
  'secrecy_grading',
  'addressee_stack',
  'routing_line',
  'annexure_spine',
  'signature_ladder',
  'signature_block',
]

/** What each piece is, in the officer's terms rather than the column name. */
const WHAT_IT_IS: Record<string, string> = {
  letterhead:
    'The printed head of a despatched document, and the contact strip at the foot of its last page. A noting carries neither — it never leaves the factory.',
  file_number_line: 'The सं: line, and how this factory composes a file number.',
  date_line: 'The दिनांक line, and where it sits.',
  secrecy_grading: 'गोपनीय, and the escalation banner beneath it where one is used.',
  addressee_stack: 'सेवा में, and the modes of despatch under it.',
  routing_line: 'द्वारा- — the section a letter passes through to reach the addressee.',
  annexure_spine: 'How annexures are headed and numbered.',
  signature_ladder:
    'The posts that initial a noting in turn, down the left margin, bottom to top. Posts only — the holder signs above the printed designation by hand, so no name is stored here and none is printed.',
  signature_block:
    "A despatched letter's closing — भवदीय, the parenthesised name, the कृते line. Not the ladder above: a letter is signed once, a noting is initialled by everyone it climbs.",
}

/**
 * Which fields of a block's content are text an administrator edits.
 *
 * A block's content also carries the notes taken while reading it off a real
 * sample — `extraction`, `source_samples`, `layout_note`, `observed`. Those are
 * kept and shown, and they are **not** editable here: they are a record of what
 * was seen on a particular document in a particular year, and an office
 * correcting its letterhead is not correcting that.
 */
const EDITABLE = new Set([
  'lines_hi',
  'lines_en',
  'posts_hi',
  'posts_en',
  'footer_strip_hi',
  'footer_strip_en',
  'template',
  'template_en',
])

/**
 * The order the fields of one block are shown in.
 *
 * **Hindi first, always.** Hindi is the document of record and English is a
 * reference reading of it; listing the reference above the instrument invites
 * an administrator to confirm the translation and skim the thing that is
 * actually served. Sorting by anything else — including the order the JSON
 * happens to hold — produced exactly that.
 */
const FIELD_ORDER = [
  'template',
  'lines_hi',
  'posts_hi',
  'footer_strip_hi',
  'template_en',
  'lines_en',
  'posts_en',
  'footer_strip_en',
]

/** A content field's label, where the key alone is not plain enough. */
const FIELD_LABEL: Record<string, string> = {
  lines_hi: 'Lines — Hindi',
  lines_en: 'Lines — English',
  posts_hi: 'Posts, bottom to top — Hindi',
  posts_en: 'Posts, bottom to top — English',
  footer_strip_hi: 'Foot of the last page — Hindi',
  footer_strip_en: 'Foot of the last page — English',
  template: 'As it prints',
  template_en: 'As it prints — English',
}

type Content = Record<string, unknown>

function isTextList(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((entry) => typeof entry === 'string')
}

/** The editable fields this particular block actually holds. */
function textFields(content: Content): [string, string[]][] {
  return Object.entries(content)
    .filter(([key]) => EDITABLE.has(key))
    .map<[string, string[]]>(([key, value]) => {
      if (isTextList(value)) return [key, value]
      if (typeof value === 'string') return [key, value.length ? value.split('\n') : []]
      return [key, []]
    })
    .filter(([, lines]) => lines.length > 0)
    .sort(
      (a, b) =>
        (FIELD_ORDER.indexOf(a[0]) + 1 || FIELD_ORDER.length + 1) -
        (FIELD_ORDER.indexOf(b[0]) + 1 || FIELD_ORDER.length + 1),
    )
}

/** Everything else the block carries — shown, never edited. See `EDITABLE`. */
function notes(content: Content): [string, string][] {
  return Object.entries(content)
    .filter(([key, value]) => !EDITABLE.has(key) && typeof value === 'string' && value.trim())
    .map<[string, string]>(([key, value]) => [key, String(value)])
}

/**
 * What this factory's documents default to.
 *
 * `unit.default_document_language` has been read on every generation since the
 * English track landed — `body.language or unit.default_document_language or
 * "hi"` — and had no surface at all, so the only way to set it was an UPDATE
 * against the database. That is how Tiruchirapalli came to be an English
 * factory on 17 September 2026. A setting the office cannot see is a setting
 * the office does not have.
 *
 * **A default, never a decision already taken.** It changes what the next
 * generation starts from and touches no document already generated: the
 * language a version was produced in is pinned on that version, because a
 * document is the language it was issued in.
 */
function FactoryDefaults() {
  const client = useQueryClient()
  const settings = useQuery({ queryKey: ['factory'], queryFn: () => api.readFactorySettings() })
  const save = useMutation({
    mutationFn: (language: string) =>
      api.updateFactorySettings({ default_document_language: language }),
    onSuccess: (fresh) => {
      client.setQueryData(['factory'], fresh)
      // Every language menu on every document screen reads from the same row.
      void client.invalidateQueries({ queryKey: ['version'] })
    },
  })

  const current = settings.data?.default_document_language ?? 'hi'
  return (
    <section className="card" data-testid="factory-defaults">
      <header>
        <h2>Documents</h2>
        <span className="small muted">{settings.data?.name ?? ''}</span>
      </header>
      <div className="body">
        <div className="field">
          <label htmlFor="default-language">Generate in</label>
          <select
            id="default-language"
            value={current}
            disabled={save.isPending || settings.isLoading}
            onChange={(event) => save.mutate(event.target.value)}
          >
            {(settings.data?.languages ?? []).map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <p className="small muted">
          What a document starts in when the officer does not choose. They may still pick the
          other language on any document the office has published in it — and a document already
          generated keeps the language it was generated in, because that is the language it was
          issued in.
        </p>
        {current === 'en' ? (
          <p className="small faint">
            English is published document by document. Where this factory has no English text for
            a document, its menu says so and the document is generated in Hindi.
          </p>
        ) : null}
        <ErrorEnvelope error={save.error ?? settings.error} />
      </div>
    </section>
  )
}

export function Settings() {
  const blocks = useQuery({ queryKey: ['org-blocks'], queryFn: () => api.listOrgBlocks() })

  // One row per key: the draft if there is one, else the newest published. The
  // list arrives as every version of every key, and showing all of them turns a
  // screen about nine pieces of stationery into a version history.
  const current = new Map<string, OrgBlockView>()
  for (const block of blocks.data ?? []) {
    const held = current.get(block.key)
    if (
      !held ||
      (block.status === 'DRAFT' && held.status !== 'DRAFT') ||
      (block.status === held.status && block.version > held.version)
    ) {
      current.set(block.key, block)
    }
  }
  const rows = [...current.values()].sort(
    (a, b) =>
      (ORDER.indexOf(a.key) + 1 || ORDER.length + 1) -
      (ORDER.indexOf(b.key) + 1 || ORDER.length + 1),
  )
  const unconfirmed = rows.filter((row) => !row.confirmed).length

  return (
    <div className="stack">
      <header className="pagehead">
        <h1>Settings</h1>
        <p className="muted">
          The factory&rsquo;s own stationery. Set here once; every template refers to it rather
          than carrying its own copy, so a corrected address is corrected everywhere.
        </p>
      </header>

      <FactoryDefaults />

      {blocks.isLoading ? <p className="muted">Reading the stationery…</p> : null}
      <ErrorEnvelope error={blocks.error} />

      {rows.length === 0 && !blocks.isLoading ? (
        <section className="card">
          <div className="body">
            <p>
              <strong>This factory has supplied no stationery.</strong> Its documents render the
              masthead, the signature block and the approval ladder as officer blanks — visibly,
              so nobody mistakes an unset letterhead for a document that has none.
            </p>
            <p className="small faint">
              Another factory&rsquo;s letterhead is never substituted. Printing one
              office&rsquo;s masthead and address on another&rsquo;s served instrument is a
              worse answer than the blank.
            </p>
          </div>
        </section>
      ) : null}

      {unconfirmed > 0 ? (
        <section className="card">
          <div className="body">
            <p>
              <strong>
                {unconfirmed} of {rows.length} awaiting the office&rsquo;s confirmation.
              </strong>{' '}
              These were read off real documents this office issued, which is evidence of what
              it prints — not the office&rsquo;s word that it is still current. Until each is
              signed off, every document generated carries office text nobody has confirmed, and
              the officer can see that on the document.
            </p>
          </div>
        </section>
      ) : null}

      {rows.map((block) => (
        <Furniture key={block.key} block={block} />
      ))}
    </div>
  )
}

function Furniture({ block }: { block: OrgBlockView }) {
  const client = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState<Record<string, string>>({})

  const content = (block.content ?? {}) as Content
  const fields = textFields(content)
  const extra = notes(content)
  const refresh = () => void client.invalidateQueries({ queryKey: ['org-blocks'] })

  const open = useMutation({
    mutationFn: () => api.openOrgBlockDraft(block.key),
    onSuccess: (row) => {
      const opened = (row.content ?? {}) as Content
      setDraft(Object.fromEntries(textFields(opened).map(([k, v]) => [k, v.join('\n')])))
      setEditing(true)
      refresh()
    },
  })

  const save = useMutation({
    mutationFn: () => {
      // Back to the shape it arrived in. A field stored as a list stays a list;
      // one stored as a single string is rejoined. Writing a list where the
      // renderer expects a string is how a letterhead comes back as `[object
      // Object]` on a served document.
      const next: Content = { ...content }
      for (const [key, text] of Object.entries(draft)) {
        const lines = text.split('\n').filter((line) => line.trim().length > 0)
        next[key] = isTextList(content[key]) ? lines : lines.join('\n')
      }
      return api.editOrgBlock(block.key, { preflight: false, content: next })
    },
    onSuccess: refresh,
  })

  const publish = useMutation({
    mutationFn: (change_note: string) => api.publishOrgBlock(block.key, { preflight: false, change_note }),
    onSuccess: () => {
      setEditing(false)
      refresh()
    },
  })

  const confirm = useMutation({
    mutationFn: () => api.confirmOrgBlock(block.id),
    onSuccess: refresh,
  })

  const isDraft = block.status === 'DRAFT'

  return (
    <section className="card">
      <header>
        <h2 className="mono">{block.key}</h2>
        <span className="small muted">
          v{block.version} · {block.status}
          {block.confirmed ? (
            <>
              {' · confirmed by '}
              {block.confirmed_by}
              {block.confirmed_at ? ` · ${readDay(block.confirmed_at)}` : ''}
            </>
          ) : (
            <>
              {' '}
              · <span className="chip warn">pending confirmation</span>
            </>
          )}
        </span>
      </header>
      <div className="body">
        <p className="small muted">{WHAT_IT_IS[block.key] ?? block.label}</p>

        <ErrorEnvelope error={open.error ?? save.error ?? publish.error ?? confirm.error} />

        {!editing ? (
          <>
            {fields.length === 0 ? (
              <p className="small faint">
                This block holds no text lines — only the notes taken when it was read. Nothing
                here to edit.
              </p>
            ) : (
              fields.map(([key, lines]) => (
                <div key={key} className="stack-tight">
                  <div className="small faint">{FIELD_LABEL[key] ?? key}</div>
                  <pre className="deva sample">{lines.join('\n')}</pre>
                </div>
              ))
            )}
            <div className="row">
              <button
                type="button"
                className="quiet"
                onClick={() => open.mutate()}
                disabled={open.isPending || fields.length === 0}
              >
                {isDraft ? 'Continue the draft' : 'Edit'}
              </button>
              {block.status === 'PUBLISHED' && !block.confirmed ? (
                <button
                  type="button"
                  className="quiet"
                  onClick={() => confirm.mutate()}
                  disabled={confirm.isPending}
                >
                  Confirm with the office
                </button>
              ) : null}
            </div>
          </>
        ) : (
          <>
            <p className="small muted">
              One line per line of the document. A blank line is dropped, so an accidental
              return does not print an empty row on a served instrument.
            </p>
            {Object.entries(draft).map(([key, text]) => (
              <div key={key} className="stack-tight">
                <label className="small faint" htmlFor={`${block.key}-${key}`}>
                  {FIELD_LABEL[key] ?? key}
                </label>
                <textarea
                  id={`${block.key}-${key}`}
                  className="deva"
                  rows={Math.min(12, Math.max(3, text.split('\n').length + 1))}
                  value={text}
                  onChange={(event) =>
                    setDraft((held) => ({ ...held, [key]: event.target.value }))
                  }
                />
              </div>
            ))}
            <div className="row">
              <button type="button" onClick={() => save.mutate()} disabled={save.isPending}>
                {save.isPending ? 'Saving…' : 'Save the draft'}
              </button>
              <button type="button" className="quiet" onClick={() => setEditing(false)}>
                Stop editing
              </button>
            </div>
            <PublishBox
              label="Publish this stationery"
              busy={publish.isPending}
              onPublish={(note) => publish.mutate(note)}
            />
            <p className="small faint">
              Publishing affects <strong>documents generated from now on</strong>. Everything
              already produced keeps the stationery it was made with, so a noting from an
              earlier year reprints with that year&rsquo;s letterhead rather than this
              one&rsquo;s.
            </p>
          </>
        )}

        {extra.length > 0 ? (
          <details>
            <summary className="small faint">
              What was read, and from which document ({extra.length})
            </summary>
            {extra.map(([key, value]) => (
              <div key={key} className="stack-tight">
                <div className="small faint mono">{key}</div>
                <p className="small">{value}</p>
              </div>
            ))}
            <p className="small faint">
              Kept, and not editable here. These are notes about a particular document in a
              particular year; an office correcting its letterhead is not correcting them.
            </p>
          </details>
        ) : null}
      </div>
    </section>
  )
}
